import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaLinkedin, FaInstagram, FaEnvelope, FaPhoneAlt } from 'react-icons/fa';
import { staggerContainer, fadeInUp, fadeInLeft, viewport } from '../../utils/animations';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gradient-to-b from-gray-900 to-black border-t border-gray-800">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              radial-gradient(circle at 1px 1px, rgba(255,255,255,0.02) 1px, transparent 0)
            `,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-gray-800 to-gray-950 rounded-full blur-3xl opacity-20" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-gray-900 to-black rounded-full blur-3xl opacity-20" />
      </div>

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Company Info */}
          <motion.div variants={fadeInLeft} className="space-y-6">
            <Link to="/" className="inline-flex items-center gap-2">
              <img
                src="/logo.png"
                alt="Zynexis logo"
                style={{ height: '28px', width: 'auto', filter: 'brightness(1.1) saturate(1.2)' }}
              />
              <h3 className="text-2xl font-black tracking-tight">
                <span className="text-white">Zy</span>
                <span className="bg-gradient-to-r from-gray-300 to-gray-400 bg-clip-text text-transparent">nexis</span>
              </h3>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              Building intelligent software that scales. We partner with ambitious businesses to create AI-powered solutions that drive real results.
            </p>
            <div className="flex gap-4">
              {[
                { icon: FaLinkedin, href: 'https://www.linkedin.com/company/zynexis-technologies', label: 'LinkedIn' },
                { icon: FaInstagram, href: 'https://www.instagram.com/zynexis_technologies/', label: 'Instagram' },
                { icon: FaEnvelope, href: 'mailto:contact@zynexistechnologies.com', label: 'Email' },
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="group relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-900 rounded-lg blur-md opacity-0 group-hover:opacity-50 transition-opacity duration-300" />
                  <div className="relative w-10 h-10 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg flex items-center justify-center text-gray-400 group-hover:text-white group-hover:border-gray-700 transition-all duration-300">
                    <social.icon className="text-lg" />
                  </div>
                </a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={fadeInUp} className="space-y-6">
            <h4 className="text-white font-bold text-lg">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: 'Services', path: '/services' },
                { name: 'Projects', path: '/projects' },
                { name: 'About Us', path: '/about' },
                { name: 'Contact', path: '/contact' },
              ].map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-white transition-colors duration-300 inline-flex items-center gap-2 group link-underline"
                  >
                    <span className="w-1 h-1 bg-gray-600 rounded-full group-hover:bg-gray-400 transition-colors duration-300" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div variants={fadeInUp} className="space-y-6">
            <h4 className="text-white font-bold text-lg">Services</h4>
            <ul className="space-y-3">
              {[
                { name: 'AI & Automation', path: '/services' },
                { name: 'Product Engineering', path: '/services' },
                { name: 'Mobile Development', path: '/services' },
                { name: 'Cloud & DevOps', path: '/services' },
                { name: 'Strategy Consulting', path: '/services' },
              ].map((service, index) => (
                <li key={index}>
                  <Link
                    to={service.path}
                    className="text-gray-400 hover:text-white transition-colors duration-300 inline-flex items-center gap-2 group link-underline"
                  >
                    <span className="w-1 h-1 bg-gray-600 rounded-full group-hover:bg-gray-400 transition-colors duration-300" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div variants={fadeInUp} className="space-y-6">
            <h4 className="text-white font-bold text-lg">Get in Touch</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="mailto:contact@zynexistechnologies.com"
                  className="text-gray-400 hover:text-white transition-colors duration-300 inline-flex items-center gap-2 group link-underline"
                >
                  <FaEnvelope className="text-gray-600 group-hover:text-gray-400 transition-colors duration-300" />
                  contact@zynexistechnologies.com
                </a>
              </li>
              <li className="space-y-1.5">
                {['+91 87997 65489', '+91 98117 21537', '+91 93488 63495'].map((num) => (
                  <a
                    key={num}
                    href={`tel:${num.replace(/\s/g, '')}`}
                    className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <FaPhoneAlt className="text-gray-600 group-hover:text-gray-400 transition-colors duration-300 flex-shrink-0" />
                    {num}
                  </a>
                ))}
              </li>
              <li className="text-gray-500 text-sm flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500/50 rounded-full animate-pulse" />
                Available for projects
              </li>
              <li className="text-gray-300 text-xs">
                We typically respond within 24 hours
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={viewport}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 pt-8 border-t border-gray-800"
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} Zynexis. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-500 hover:text-gray-400 text-sm transition-colors duration-300 link-underline">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-gray-400 text-sm transition-colors duration-300 link-underline">
                Terms of Service
              </Link>
              <Link to="/how-we-operate" className="text-gray-500 hover:text-gray-400 text-sm transition-colors duration-300 link-underline">
                How We Operate
              </Link>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </footer>
  );
};

export default Footer;